import { useMemo } from "react";

import { useColorScheme, useTheme } from "@mui/material/styles";

import { THEME_COLOR_CONFIG } from "config/themeColors";
import { isDarkColorScheme, withAlpha } from "utils/colorUtils";

const SURFACE = THEME_COLOR_CONFIG.surface;

/** @typedef {'table' | 'panel' | 'subtle'} SurfaceBorderRole */
/** @typedef {'table' | 'panel' | 'subtle' | 'strong' | 'default'} SurfaceBorderInput */

const BORDER_ROLE = {
  table: "table",
  strong: "table",
  panel: "panel",
  default: "panel",
  subtle: "subtle",
};

function resolveBorderRole(role = "panel") {
  return BORDER_ROLE[role] ?? "panel";
}

function primaryColor(theme) {
  return theme.vars?.palette?.primary?.main ?? theme.palette.primary.main;
}

/**
 * Primary-tinted border color (CSS-var safe).
 * @param {SurfaceBorderInput} [role] — `table` for data tables; `panel` for cards/boxes.
 */
export function surfaceBorderColor(theme, colorSchemeMode, role = "panel") {
  const key = resolveBorderRole(role);
  const isDark = isDarkColorScheme(theme, colorSchemeMode);
  const alpha = isDark
    ? SURFACE.borderAlpha[key].dark
    : SURFACE.borderAlpha[key].light;
  return withAlpha(primaryColor(theme), alpha);
}

/** MUI theme override context (no colorScheme hook). */
export function muiSurfaceBorderColor(theme, role = "panel") {
  return surfaceBorderColor(theme, theme.palette.mode, role);
}

export function surfacePanelBackground(theme, colorSchemeMode, variant = "muted") {
  const isDark = isDarkColorScheme(theme, colorSchemeMode);
  const cfg = SURFACE.panelBgAlpha[variant] ?? SURFACE.panelBgAlpha.muted;
  const alpha = isDark ? cfg.dark : cfg.light;
  if (!isDark && alpha === 0) {
    return theme.palette.background.paper;
  }
  return withAlpha(primaryColor(theme), alpha);
}

export function surfaceBorderSx(theme, colorSchemeMode, role = "panel") {
  return {
    border: 1,
    borderColor: surfaceBorderColor(theme, colorSchemeMode, role),
  };
}

export function surfaceBorderTopSx(theme, colorSchemeMode, role = "panel") {
  return {
    borderTop: 1,
    borderColor: surfaceBorderColor(theme, colorSchemeMode, role),
  };
}

/** Outer frame around a scrollable table + pagination (Devices data tab). */
export function surfaceTableFrameSx(theme, colorSchemeMode) {
  return {
    ...surfaceBorderSx(theme, colorSchemeMode, "table"),
    borderRadius: SURFACE.frame.borderRadius,
    overflow: "hidden",
  };
}

/** Table sx: header + cell borders + optional zebra body (pass `tableBodyCellSx`). */
export function surfaceTableSx(theme, colorSchemeMode, extraSx = {}) {
  const borderColor = surfaceBorderColor(theme, colorSchemeMode, "table");
  const headBorder = `${SURFACE.table.headBorderWidthPx}px solid`;

  return {
    "& .MuiTableHead-root": {
      backgroundColor: SURFACE.table.headerBackground,
      borderTop: "none",
      borderBottom: headBorder,
      borderBottomColor: borderColor,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      backgroundColor: SURFACE.table.headerBackground,
      color: "text.primary",
      borderColor,
    },
    "& .MuiTableCell-root": {
      borderColor,
    },
    ...extraSx,
  };
}

/** Top rule above table pagination / summary bars. */
export function surfaceTableFooterSx(theme, colorSchemeMode) {
  return {
    borderTop: 1,
    borderColor: surfaceBorderColor(theme, colorSchemeMode, "table"),
  };
}

/** Top rule on panel footers (non-table sections). */
export function surfacePanelFooterSx(theme, colorSchemeMode) {
  return {
    borderTop: 1,
    borderColor: surfaceBorderColor(theme, colorSchemeMode, "panel"),
  };
}

/** Dark mobile list rows: flat bottom separator matching data tables. */
export function surfaceListRowSeparatorSx(theme, colorSchemeMode) {
  return {
    borderRadius: 0,
    border: "none",
    borderBottom: 1,
    borderColor: surfaceBorderColor(theme, colorSchemeMode, "table"),
  };
}

/** Bordered empty-state / content shell (panel role). */
export function surfacePanelFrameSx(theme, colorSchemeMode) {
  return {
    ...surfaceBorderSx(theme, colorSchemeMode, "panel"),
    borderRadius: SURFACE.frame.borderRadius,
  };
}

/** Bordered panel shell (readings split columns, bordered sections). */
export function surfacePanelShellSx(
  theme,
  colorSchemeMode,
  { borderRole = "panel", bgVariant = "muted" } = {},
) {
  return {
    boxSizing: "border-box",
    borderRadius: SURFACE.frame.borderRadius,
    ...surfaceBorderSx(theme, colorSchemeMode, borderRole),
    bgcolor: surfacePanelBackground(theme, colorSchemeMode, bgVariant),
    color: "text.primary",
  };
}

/**
 * Hook for pages — table frames, panel borders, pagination rules.
 * @param {SurfaceBorderInput} [defaultRole] — default `panel`; pass `table` on table-heavy pages.
 */
export function useSurfaceTheme(defaultRole = "panel") {
  const theme = useTheme();
  const { mode: colorSchemeMode } = useColorScheme();

  const tableBorderColor = useMemo(
    () => surfaceBorderColor(theme, colorSchemeMode, "table"),
    [theme, colorSchemeMode],
  );

  const panelBorderColor = useMemo(
    () => surfaceBorderColor(theme, colorSchemeMode, "panel"),
    [theme, colorSchemeMode],
  );

  const borderColor = useMemo(
    () => surfaceBorderColor(theme, colorSchemeMode, defaultRole),
    [theme, colorSchemeMode, defaultRole],
  );

  return useMemo(
    () => ({
      theme,
      colorSchemeMode,
      tableBorderColor,
      panelBorderColor,
      borderColor,
      /** @deprecated use tableBorderColor */
      strongBorderColor: tableBorderColor,
      surfaceBorderSx: (role = defaultRole) =>
        surfaceBorderSx(theme, colorSchemeMode, role),
      surfaceBorderTopSx: (role = defaultRole) =>
        surfaceBorderTopSx(theme, colorSchemeMode, role),
      tableFrameSx: surfaceTableFrameSx(theme, colorSchemeMode),
      tableSx: (extraSx = {}) => surfaceTableSx(theme, colorSchemeMode, extraSx),
      panelShellSx: (options) => surfacePanelShellSx(theme, colorSchemeMode, options),
      tableFooterSx: surfaceTableFooterSx(theme, colorSchemeMode),
      panelFooterSx: surfacePanelFooterSx(theme, colorSchemeMode),
      listRowSeparatorSx: surfaceListRowSeparatorSx(theme, colorSchemeMode),
      panelFrameSx: surfacePanelFrameSx(theme, colorSchemeMode),
      /** @deprecated use surfaceBorderSx / tableFrameSx */
      surfaceBorderSxLegacy: surfaceBorderSx(theme, colorSchemeMode, defaultRole),
      surfaceBorderTopSxLegacy: surfaceBorderTopSx(theme, colorSchemeMode, defaultRole),
      tableFrameSxLegacy: surfaceTableFrameSx(theme, colorSchemeMode),
    }),
    [theme, colorSchemeMode, defaultRole, tableBorderColor, panelBorderColor, borderColor],
  );
}

// ——— Backwards-compatible aliases (prefer surface* names in new code) ———

export const themeSurfaceBorderColor = surfaceBorderColor;
export const themeSurfaceBorderSx = surfaceBorderSx;
export const themeSurfaceBorderTopSx = surfaceBorderTopSx;
export const themeTableFrameSx = surfaceTableFrameSx;
export const themeTableSx = surfaceTableSx;
export const useThemeBorders = useSurfaceTheme;

export function readingsPanelBorderColor(theme, colorSchemeMode, side = "live") {
  return surfaceBorderColor(
    theme,
    colorSchemeMode,
    side === "live" ? "table" : "panel",
  );
}
